import React, { useEffect, useRef, useState } from "react";

const AnnouncementTicker = ({
  announcements = [],
  backgroundColor = "#fff",
  textColor = "#212529",
  speed = 100, // pixels per second
  gap = 1.2, // seconds between announcements
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);
  const [animationDuration, setAnimationDuration] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);

  const containerRef = useRef(null);
  const textRef = useRef(null);
  const loopTimer = useRef(null);

  //console.log("Ticker received announcements:", announcements, "currentIndex:", currentIndex);

  const currentText =
    announcements[currentIndex]?.title || announcements[currentIndex] || "";

  // --- Measure container + text to calculate movement distance ---
  useEffect(() => {
    if (!announcements.length || !containerRef.current || !textRef.current)
      return;

    const cWidth = containerRef.current.offsetWidth;
    const tWidth = textRef.current.scrollWidth;
    const totalDistance = cWidth + tWidth; // total pixels to travel
    const duration = totalDistance / speed;

    setContainerWidth(cWidth);
    setTextWidth(tWidth);
    setAnimationDuration(duration);
    setAnimationKey((k) => k + 1); // restart animation
  }, [currentIndex, announcements, speed]);

  // --- Cycle announcements after scroll finishes ---
  useEffect(() => {
    if (!announcements.length || animationDuration === 0) return;

    const runLoop = () => {
      // Force restart of animation
      setAnimationKey((k) => k + 1);

      // Wait till animation + gap ends, then move to next
      loopTimer.current = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % announcements.length);
        runLoop(); // recursive call keeps it going infinitely
      }, (animationDuration + gap) * 1000);
    };

    runLoop();

    return () => {
      if (loopTimer.current) clearTimeout(loopTimer.current);
    };
  }, [announcements, animationDuration, gap]);

  if (!currentText) return null;

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden flex items-center py-[2svh]"
      style={{
        backgroundColor,
        color: textColor,
      }}
    >
      <div
        ref={textRef}
        key={`${currentIndex}-${animationKey}`}
        className="absolute whitespace-nowrap text-[3svh] font-medium"
        style={{
          transform: `translateX(${containerWidth}px)`,
          animation: `scrollTicker-${animationKey} ${animationDuration}s linear forwards`,
        }}
      >
        {currentText}
      </div>

      <style>{`
        @keyframes scrollTicker-${animationKey} {
          0% { transform: translateX(${containerWidth}px); }
          100% { transform: translateX(-${textWidth}px); }
        }
      `}</style>
    </div>
  );
};

export default AnnouncementTicker;